import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  ApiErrorShape,
  CareerRoleFullPictureResponse,
  CareerRoleSkillResourceCountsResponse,
  SkillResourceCountSummaryDto,
  SkillWithResourcesResponse
} from '../../../../models';

export interface CareerRoleFullPictureState {
  slug: string;
  picture: CareerRoleFullPictureResponse | null;
  loading: boolean;
  refreshing: boolean;
  error: string;
  fetchedAt: number | null;
}

export interface CareerRoleFullPictureLoadOptions {
  force?: boolean;
  background?: boolean;
}

const DEBOUNCE_MS = 300;

@Injectable({ providedIn: 'root' })
export class CareerRoleFullPictureStore {
  private readonly api = inject(CsrWebApiService);

  private readonly revision = signal(0);
  private readonly entries = new Map<string, CareerRoleFullPictureState>();
  private readonly inFlight = new Map<string, Promise<CareerRoleFullPictureResponse | null>>();
  private readonly debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

  getState(slug: string): CareerRoleFullPictureState | undefined {
    this.revision();
    return this.entries.get(slug);
  }

  async load(
    slug: string,
    options: CareerRoleFullPictureLoadOptions = {}
  ): Promise<CareerRoleFullPictureResponse | null> {
    if (!slug) {
      return null;
    }

    const existing = this.entries.get(slug);
    const hasPicture = !!existing?.picture;
    const background = options.background ?? hasPicture;

    if (!options.force && hasPicture && !background) {
      return existing!.picture;
    }

    const inFlight = this.inFlight.get(slug);
    if (inFlight && !options.force) {
      return inFlight;
    }

    this.patch(slug, {
      loading: !background && !hasPicture,
      refreshing: background && hasPicture,
      error: ''
    });

    const task = this.fetchFullPicture(slug);
    this.inFlight.set(slug, task);
    try {
      return await task;
    } finally {
      this.inFlight.delete(slug);
    }
  }

  invalidate(slug: string, options: { refresh?: boolean } = {}): void {
    if (!slug) {
      return;
    }
    if (!options.refresh) {
      return;
    }
    const existing = this.debounceTimers.get(slug);
    if (existing) {
      clearTimeout(existing);
    }
    const timer = setTimeout(() => {
      this.debounceTimers.delete(slug);
      void this.load(slug, { background: true });
    }, DEBOUNCE_MS);
    this.debounceTimers.set(slug, timer);
  }

  async refreshResourceCounts(slug: string): Promise<void> {
    if (!slug) {
      return;
    }
    try {
      const response = await firstValueFrom(this.api.getCareerRoleSkillResourceCounts(slug));
      this.patchResourceCounts(slug, response);
    } catch (error) {
      const message = this.errorMessage(error);
      this.patch(slug, { error: message });
    }
  }

  patchResourceCounts(slug: string, response: CareerRoleSkillResourceCountsResponse): void {
    const existing = this.entries.get(slug);
    if (!existing?.picture) {
      void this.load(slug, { background: true });
      return;
    }

    const picture = this.applyResourceCounts(existing.picture, response.countsBySkillId);
    this.patch(slug, { picture, fetchedAt: Date.now(), error: '' });
  }

  clear(slug?: string): void {
    if (slug) {
      this.entries.delete(slug);
      const timer = this.debounceTimers.get(slug);
      if (timer) {
        clearTimeout(timer);
        this.debounceTimers.delete(slug);
      }
    } else {
      this.entries.clear();
      for (const timer of this.debounceTimers.values()) {
        clearTimeout(timer);
      }
      this.debounceTimers.clear();
    }
    this.revision.update((v) => v + 1);
  }

  onBackgroundJobComplete(roleSlug: string | undefined, operationType?: string): void {
    if (!roleSlug) {
      return;
    }
    if (this.isResourceOnlyOperation(operationType)) {
      void this.refreshResourceCounts(roleSlug);
      return;
    }
    this.invalidate(roleSlug, { refresh: true });
  }

  private isResourceOnlyOperation(operationType?: string): boolean {
    if (!operationType) {
      return false;
    }
    const resourceOps = new Set([
      'populate_role_resources',
      'populate_verified_role_resources',
      'populate_skill_resources',
      'automated_populate_skill_resources',
      'enrich_staged_resources',
      'reprocess_published_resources',
      'validate_committed_resources',
      'zero_resource_skill_sweep'
    ]);
    return resourceOps.has(operationType);
  }

  private async fetchFullPicture(slug: string): Promise<CareerRoleFullPictureResponse | null> {
    try {
      const picture = await firstValueFrom(this.api.getCareerRoleSkillsProfile(slug));
      this.patch(slug, {
        picture,
        loading: false,
        refreshing: false,
        error: '',
        fetchedAt: Date.now()
      });
      return picture;
    } catch (error) {
      const message = this.errorMessage(error);
      const existing = this.entries.get(slug);
      this.patch(slug, {
        picture: existing?.picture ?? null,
        loading: false,
        refreshing: false,
        error: message
      });
      return existing?.picture ?? null;
    }
  }

  private applyResourceCounts(
    picture: CareerRoleFullPictureResponse,
    countsBySkillId: Record<string, SkillResourceCountSummaryDto>
  ): CareerRoleFullPictureResponse {
    const patchSkill = (skill: SkillWithResourcesResponse): SkillWithResourcesResponse => {
      const counts = countsBySkillId[skill.skill.id];
      if (!counts) {
        return skill;
      }
      return {
        ...skill,
        resourceCount: counts.resourceCount,
        topicResourceCounts: counts.topicResourceCounts
      };
    };

    return {
      ...picture,
      seniorityProfiles: picture.seniorityProfiles.map((profile) => ({
        ...profile,
        skills: profile.skills.map(patchSkill)
      })),
      specializations: picture.specializations.map((spec) => ({
        ...spec,
        seniorityProfiles: spec.seniorityProfiles.map((profile) => ({
          ...profile,
          skills: profile.skills.map(patchSkill)
        }))
      }))
    };
  }

  private patch(slug: string, partial: Partial<CareerRoleFullPictureState>): void {
    const current = this.entries.get(slug) ?? {
      slug,
      picture: null,
      loading: false,
      refreshing: false,
      error: '',
      fetchedAt: null
    };
    this.entries.set(slug, { ...current, slug, ...partial });
    this.revision.update((v) => v + 1);
  }

  private errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as ApiErrorShape;
      if (body?.errors) {
        const first = Object.values(body.errors)[0];
        if (typeof first === 'string' && first.length > 0) {
          return first;
        }
      }
      return body?.title ?? `Request failed (${error.status})`;
    }

    return 'Unexpected error';
  }
}